import { useState, type FormEvent } from 'react';
import type { AvailableAction, TaskDetail } from '@ssn/shared';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { TextArea } from '@/components/ui/Form';
import { InlineError, Callout } from '@/components/ui/Feedback';
import { useTransitionTask } from '@/lib/queries';
import { StatusIcon } from './status';

const INTENT_VARIANT = {
  primary: 'primary',
  positive: 'positive',
  danger: 'danger',
  secondary: 'secondary',
} as const;

/**
 * The workflow action bar.
 *
 * The actions rendered here are the ones the SERVER said this viewer may take,
 * resolved from the same transition rules it will enforce. The client never
 * decides what is allowed — it only decides what is worth drawing (§21).
 */
export function TaskActions({ task, className }: { task: TaskDetail; className?: string }) {
  const [pending, setPending] = useState<AvailableAction | null>(null);
  const transition = useTransitionTask(task.reference);

  if (task.availableActions.length === 0) return null;

  const run = (action: AvailableAction) => {
    if (action.requiresComment) {
      setPending(action);
      return;
    }
    transition.mutate({ action: action.action, comment: null, requiredChanges: null });
  };

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        {task.availableActions.map((action) => (
          <Button
            key={action.action}
            variant={INTENT_VARIANT[action.intent]}
            size="md"
            title={action.description}
            loading={transition.isPending && transition.variables?.action === action.action}
            disabled={transition.isPending}
            onClick={() => run(action)}
            icon={<StatusIcon status={action.to} />}
          >
            {action.label}
          </Button>
        ))}
      </div>

      <InlineError error={transition.error} className="mt-2.5" />

      <ActionCommentDialog
        action={pending}
        reference={task.reference}
        onClose={() => setPending(null)}
      />
    </div>
  );
}

/**
 * Collects the comment an action requires.
 *
 * §24 is explicit that a director must not be able to request changes without
 * saying what needs changing, so the submit button stays disabled until there is
 * something to say — and the server refuses an empty one regardless.
 */
function ActionCommentDialog({
  action,
  reference,
  onClose,
}: {
  action: AvailableAction | null;
  reference: number;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  const [requiredChanges, setRequiredChanges] = useState('');
  const transition = useTransitionTask(reference);

  const isRequestChanges = action?.action === 'REQUEST_CHANGES';

  const close = () => {
    setComment('');
    setRequiredChanges('');
    transition.reset();
    onClose();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!action) return;

    transition.mutate(
      {
        action: action.action,
        comment: comment.trim(),
        requiredChanges: requiredChanges.trim() || null,
      },
      { onSuccess: close },
    );
  };

  return (
    <Modal
      open={action !== null}
      onClose={close}
      title={action?.label ?? ''}
      description={action?.description}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="action-comment-form"
            variant={isRequestChanges ? 'danger' : 'primary'}
            loading={transition.isPending}
            disabled={comment.trim().length === 0}
          >
            {action?.label}
          </Button>
        </>
      }
    >
      <form id="action-comment-form" onSubmit={submit} className="space-y-4">
        {isRequestChanges && (
          <Callout tone="warning" title="The developer will be notified on Discord">
            Be specific about what must change. This comment is what they will work from.
          </Callout>
        )}

        <TextArea
          label={isRequestChanges ? 'What needs to change?' : 'Comment'}
          required
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={
            isRequestChanges
              ? 'Explain what is wrong and what you expect instead…'
              : 'Add a short explanation…'
          }
          hint="This is posted to the task conversation and recorded in the review history."
        />

        {isRequestChanges && (
          <TextArea
            label="Required changes (optional)"
            value={requiredChanges}
            onChange={(event) => setRequiredChanges(event.target.value)}
            placeholder={'- Fix the dispatch filter\n- Add a test for the map assignment'}
            hint="A structured checklist, kept alongside the review record."
          />
        )}

        <InlineError error={transition.error} />
      </form>
    </Modal>
  );
}
